import { cn } from '@/lib/utils';

/**
 * A trend small enough for a table cell — twelve months of searches beside a
 * keyword. Plain SVG rather than a chart library, because a table of a
 * hundred rows should not mount a hundred charts. Ink only; the direction is
 * read from the shape, and the number beside it carries any colour.
 */
export function InlineTrend({
  data,
  width = 56,
  height = 18,
  className,
  testId,
}: {
  data: number[] | null | undefined;
  width?: number;
  height?: number;
  className?: string;
  testId?: string;
}) {
  const values = (data ?? []).filter((v) => typeof v === 'number' && Number.isFinite(v));
  if (values.length < 2) return <span className={cn('inline-block', className)} style={{ width, height }} aria-hidden />;

  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = max - min || 1;
  const pad = 1.5;
  const step = (width - pad * 2) / (values.length - 1);
  const y = (v: number) => pad + (height - pad * 2) * (1 - (v - min) / span);
  const points = values.map((v, i) => `${(pad + i * step).toFixed(1)},${y(v).toFixed(1)}`);
  const area = `M${pad},${height - pad} L${points.join(' L')} L${(pad + (values.length - 1) * step).toFixed(1)},${height - pad} Z`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={cn('shrink-0 overflow-visible text-foreground', className)}
      aria-hidden
      data-testid={testId}
    >
      <path d={area} className="fill-current opacity-[0.07]" />
      <polyline points={points.join(' ')} fill="none" stroke="currentColor" strokeWidth={1.25} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}
